import type { ReactNode } from 'react';
import { useParams, useNavigate } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { Tag, Typography } from 'antd';
import { DetailPage, SectionHeader, space } from '@viq-admin/ui';
import { getUser } from '../../api/users';
import { CompanyLink } from '../../components/EntityLinks';
import { LeadsMiniTable } from '../../components/LeadsMiniTable';
import { useTrackRecent } from '../../recent/recentItems';
import { ConferencesPanel } from './ConferencesPanel';
import { DangerZone } from './DangerZone';
import { RoleTag } from './RoleTag';
import { UserDevices } from './UserDevices';
import { UserFacts } from './UserFacts';
import { UserHeaderActions } from './UserHeaderActions';

/**
 * The user page, built so "jane says the app won't sync" is answered here
 * without leaving it. One scrolling page, no tabs: every question about a
 * user is one scroll away rather than one click away.
 *
 *   Header      name (+ Inactive), role · company link · copyable email;
 *               Deactivate / Reactivate and Edit (UserHeaderActions)
 *   Summary     status, last login, created, company, role (UserFacts)
 *   Devices     the user's app installs and their sync state (UserDevices)
 *   Conferences assignments, each linked, with Remove (ConferencesPanel)
 *   Recent leads the latest captures by this rep (LeadsMiniTable)
 *   Danger zone Purge (DangerZone)
 *
 * The record comes from GET /v1/superadmin/users/{oid} — a custom, non-CRUD
 * endpoint that adds conference assignments and the company name — so it
 * uses react-query directly rather than Refine's `useOne`. Devices and leads
 * load on their own, each with its own loading and error state, so one
 * failing source never blanks the page. Both are fetched with the user's own
 * company or oid, never the "Operate as" scope, so the page reads the same
 * whatever scope is set.
 *
 * A 404 is "not found"; any other failure (403, 500, network) is an error
 * with Retry, not a claim that the user doesn't exist.
 *
 * The route uses `:id` (App.tsx resource table); resolve `:oid` too so this
 * works whichever param the wired route uses.
 */
export function UserDetailView() {
  const { id, oid } = useParams<{ id?: string; oid?: string }>();
  const userId = id ?? oid;
  const navigate = useNavigate();
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['user', userId],
    queryFn: () => getUser(userId!),
    enabled: !!userId,
  });

  const name = data ? data.name || data.email : '';
  const notFound =
    !isLoading && ((error as { statusCode?: number } | null)?.statusCode === 404 || (!error && !data));

  useTrackRecent(
    data ? { kind: 'user', id: data.oid, label: name, sublabel: data.email } : null,
  );

  return (
    <DetailPage
      title={
        data ? (
          <>
            {name}
            {data.deactivatedAt ? <Tag style={{ marginInlineStart: space.sm }}>Inactive</Tag> : null}
          </>
        ) : (
          'User'
        )
      }
      subtitle={
        data ? (
          <>
            <RoleTag role={data.role} />
            <CompanyLink id={data.companyId} name={data.companyName} /> ·{' '}
            <Typography.Text type="secondary" copyable={{ text: data.email }}>
              {data.email}
            </Typography.Text>
          </>
        ) : undefined
      }
      breadcrumbs={[{ label: 'Users', href: '/users' }, { label: name || 'User' }]}
      onBack={() => navigate('/users')}
      onNavigate={(t) => navigate(t.href)}
      loading={isLoading}
      notFound={notFound}
      notFoundHref="/users"
      error={notFound ? undefined : error}
      onRetry={() => void refetch()}
      actions={
        data ? (
          <UserHeaderActions
            oid={data.oid}
            displayName={name}
            isInactive={!!data.deactivatedAt}
            onChanged={() => void refetch()}
          />
        ) : undefined
      }
      readView={
        data ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: space.xl }}>
            <UserFacts user={data} />
            <Section title="Devices">
              <UserDevices oid={data.oid} companyId={data.companyId} />
            </Section>
            <ConferencesPanel
              oid={data.oid}
              userCompanyId={data.companyId}
              conferences={data.conferences ?? []}
              onChanged={() => void refetch()}
            />
            <Section title="Recent leads">
              <LeadsMiniTable filter={{ repOid: data.oid }} hide={['rep']} />
            </Section>
            <DangerZone
              oid={data.oid}
              displayName={name}
              onPurged={() => navigate('/users')}
            />
          </div>
        ) : null
      }
    />
  );
}

/** A titled block of the page; `minWidth: 0` lets a wide table scroll inside it on a phone. */
function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section style={{ minWidth: 0 }}>
      <SectionHeader>{title}</SectionHeader>
      {children}
    </section>
  );
}
